import axios from "axios";
import { appUrl } from "../../../../../utils/config";

// ═══════════════════════════════════════════════════════════════════════════
// Document Repository data layer — wired to the Document Repository backend.
//
// All endpoints are POST, require level1Authentication, and are served under the
// current dashboard's route prefix (/CMO/* or /CMS/*). getApiBaseUrl() already
// embeds collabType (CMO / CMS), so the correct prefix is used automatically.
//   • Product dropdown  → POST /fetchDocumentRepositoryProducts
//   • Batch dropdown    → POST /fetchDocumentRepositoryBatches            ({ productId } cascade)
//   • Category dropdown → GET  /fetchDocumentCategories/true
//   • Dashboard table   → POST /fetchDocumentRepository                   ({} = all rows)
//   • View / Download   → POST /downloadCustomFile                        (schrockenCustomFileID)
//
// Every endpoint returns HTTP 200 even on failure, with the error shape
// { status: false, errorMessage, errorDescription } — so `status` is checked
// before the payload is used.
//
// The auth token is attached globally (axios.defaults.headers.common.authToken
// is set at app boot), so the bare axios instance is used here.
//
// When the middleware is unavailable or a call fails, each function returns an
// empty result with `status: false` — the screen renders an empty / error state
// rather than any placeholder or demo data.
// ═══════════════════════════════════════════════════════════════════════════

const getApiBaseUrl = () => {
  const appName = localStorage.getItem("appName");
  const network = localStorage.getItem("network");
  const collabType = localStorage.getItem("collabType");
  const roleName = localStorage.getItem("roleName");
  if (appName && network && collabType && roleName) {
    return `${appUrl}/${appName}/${network}/${collabType}/${roleName}`;
  }
  return null;
};

// ═══════════════════════════════════════════════════════════════════════════
// Batch Quality documents (static)
// ---------------------------------------------------------------------------
// The files under Documents_for_2388-OXB-A-1 and Documents_for_2785-OXB-B-1 are
// surfaced in the Document Repository table as "Quality" category documents.
// They are not served by the middleware, so they are merged into the listing
// client-side: they appear when no filter is set, when their batch is selected,
// or when the "Quality" document category is selected. For each row the document
// name is the file name and the document category is "Quality".
//
// There is no vault file behind these rows (schrockenCustomFileID is null), so
// view / download degrade gracefully in index.jsx.
// ═══════════════════════════════════════════════════════════════════════════

const QUALITY_CATEGORY = "Quality";
// The two batches below belong to the 2440 / CD19 CAR-LVV product (the same
// product used across Manufacturing Collaboration).
const QUALITY_PRODUCT = { code: "2440", description: "CD19 CAR-LVV" };

export const QUALITY_BATCH_NUMBERS = ["2388-OXB-A-1", "2785-OXB-B-1"];

// The Quality document files (.docx) are colocated with this feature under
// ./qualityDocuments and bundled by webpack (asset/resource emits a hashed URL
// per file). The same .docx URL is used both for download and for inline view
// (rendered client-side by docx-preview — see DocxPreview.jsx). require.context
// is a no-op outside webpack (e.g. Node tests), where the URLs resolve to "".
let qualityDocxCtx = null;
try {
  qualityDocxCtx = require.context("./qualityDocuments", false, /\.docx$/);
} catch (e) {
  // Non-webpack environment — asset URLs resolve to "".
}
const qualityAssetUrl = (fileName) => {
  if (!qualityDocxCtx) return "";
  try {
    return qualityDocxCtx(`./${fileName}.docx`);
  } catch (e) {
    return "";
  }
};

// Wrap a plain value in the { value, confidence } shape the "Review Digitalized
// Fields" viewer (CommonDigitizedDocument) needs to render a field.
const f = (value, confidence = 97) => ({ value, confidence });

// Build one Quality document row. documentName === the file's name; the same
// name is shown (with a DOCX badge) in the Document Type column.
const qualityDoc = ({ documentId, fileName, batchNumber, uploadDate, kvpJson }) => ({
  schrockenCustomFileID: null, // no vault file — these are served as static assets
  isDemo: true,
  documentId,
  documentName: fileName,
  documentType: fileName,
  fileName,
  fileFormat: "DOCX",
  // Bundled .docx asset URL — used for both inline view and download.
  downloadUrl: qualityAssetUrl(fileName),
  viewUrl: qualityAssetUrl(fileName),
  documentCategory: QUALITY_CATEGORY,
  productCode: QUALITY_PRODUCT.code,
  productId: QUALITY_PRODUCT.code,
  productDescription: QUALITY_PRODUCT.description,
  batchNumber,
  cmoBatchNumber: batchNumber,
  uploadDate,
  digitizedData: { kvpJson: kvpJson || {} },
});

const STATIC_QUALITY_DOCUMENTS = [
  // ── Documents_for_2388-OXB-A-1 ──
  qualityDoc({
    documentId: "BPR-2388-OXB-A-1",
    fileName: "Batch_Production_Record_2388-OXB-A-1",
    batchNumber: "2388-OXB-A-1",
    uploadDate: "2025-05-23",
    kvpJson: {
      document_type: f("Batch Production Record"),
      product_name: f("CD19 CAR-LVV"),
      batch_number: f("2388-OXB-A-1", 99),
      recipe_id: f("MBPR-2388-OXB-A-1"),
      manufacturing_date: f("23-MAY-2025"),
      batch_disposition: f("Released"),
    },
  }),
  qualityDoc({
    documentId: "CoA-2388-OXB-A-1",
    fileName: "CoA_2388-OXB-A-1",
    batchNumber: "2388-OXB-A-1",
    uploadDate: "2025-05-24",
    kvpJson: {
      document_type: f("Certificate of Analysis"),
      product_name: f("CD19 CAR-LVV"),
      batch_number: f("2388-OXB-A-1", 99),
      manufacturing_date: f("23-MAY-2025"),
      expiry_date: f("23-MAY-2027"),
      overall_result: f("Conforms"),
      release_status: f("Approved"),
    },
  }),
  qualityDoc({
    documentId: "DEV-5842",
    fileName: "Deviation_Report_DEV-5842_2388-OXB-A-1",
    batchNumber: "2388-OXB-A-1",
    uploadDate: "2025-05-16",
    kvpJson: {
      document_type: f("Deviation Report"),
      deviation_id: f("DEV-5842", 99),
      batch_number: f("2388-OXB-A-1", 99),
      severity: f("Major"),
      date_raised: f("16-MAY-2025"),
      status: f("Closed"),
    },
  }),
  qualityDoc({
    documentId: "IR-OXB-2388-001",
    fileName: "Investigation_Report_IR-OXB-2388-001_2388-OXB-A-1",
    batchNumber: "2388-OXB-A-1",
    uploadDate: "2025-05-18",
    kvpJson: {
      document_type: f("Investigation Report"),
      investigation_id: f("IR-OXB-2388-001", 99),
      batch_number: f("2388-OXB-A-1", 99),
      related_deviation: f("DEV-5842"),
      root_cause: f("Assignable cause identified"),
      status: f("Closed"),
    },
  }),
  qualityDoc({
    documentId: "PC-OXB-2388-092",
    fileName: "Planned_Change_Request_PC-OXB-2388-092_2388-OXB-A-1",
    batchNumber: "2388-OXB-A-1",
    uploadDate: "2025-05-10",
    kvpJson: {
      document_type: f("Planned Change Request"),
      change_control_id: f("PC-OXB-2388-092", 99),
      batch_number: f("2388-OXB-A-1", 99),
      change_type: f("Planned"),
      status: f("Approved"),
    },
  }),
  // ── Documents_for_2785-OXB-B-1 ──
  qualityDoc({
    documentId: "BPR-2785-OXB-B-1",
    fileName: "Batch_Production_Record_2785-OXB-B-1",
    batchNumber: "2785-OXB-B-1",
    uploadDate: "2025-10-24",
    kvpJson: {
      document_type: f("Batch Production Record"),
      product_name: f("CD19 CAR-LVV"),
      batch_number: f("2785-OXB-B-1", 99),
      recipe_id: f("MBPR-2785-OXB-B-1"),
      manufacturing_date: f("24-OCT-2025"),
      batch_disposition: f("Released"),
    },
  }),
  qualityDoc({
    documentId: "CoA-2785-OXB-B-1",
    fileName: "CoA_2785-OXB-B-1",
    batchNumber: "2785-OXB-B-1",
    uploadDate: "2025-10-25",
    kvpJson: {
      document_type: f("Certificate of Analysis"),
      product_name: f("CD19 CAR-LVV"),
      batch_number: f("2785-OXB-B-1", 99),
      manufacturing_date: f("24-OCT-2025"),
      expiry_date: f("24-OCT-2027"),
      overall_result: f("Conforms"),
      release_status: f("Approved"),
    },
  }),
  qualityDoc({
    documentId: "DEV-5970",
    fileName: "Deviation_Report_DEV-5970_2785-OXB-B-1",
    batchNumber: "2785-OXB-B-1",
    uploadDate: "2025-10-17",
    kvpJson: {
      document_type: f("Deviation Report"),
      deviation_id: f("DEV-5970", 99),
      batch_number: f("2785-OXB-B-1", 99),
      severity: f("Major"),
      date_raised: f("17-OCT-2025"),
      status: f("Closed"),
    },
  }),
  qualityDoc({
    documentId: "IR-OXB-2785-001",
    fileName: "Investigation_Report_IR-OXB-2785-001_2785-OXB-B-1",
    batchNumber: "2785-OXB-B-1",
    uploadDate: "2025-10-19",
    kvpJson: {
      document_type: f("Investigation Report"),
      investigation_id: f("IR-OXB-2785-001", 99),
      batch_number: f("2785-OXB-B-1", 99),
      related_deviation: f("DEV-5970"),
      root_cause: f("Assignable cause identified"),
      status: f("Closed"),
    },
  }),
  qualityDoc({
    documentId: "PC-OXD-2785-121",
    fileName: "Planned_Change_Request_PC-OXD-2785-121_2785-OXB-B-1",
    batchNumber: "2785-OXB-B-1",
    uploadDate: "2025-10-12",
    kvpJson: {
      document_type: f("Planned Change Request"),
      change_control_id: f("PC-OXD-2785-121", 99),
      batch_number: f("2785-OXB-B-1", 99),
      change_type: f("Planned"),
      status: f("Approved"),
    },
  }),
];

// ═══════════════════════════════════════════════════════════════════════════
// Testing documents (per-batch subfolders of ./qualityDocuments)
// ---------------------------------------------------------------------------
// The Testing documents live in Testing-2388-OXB-A-1 / Testing-2785-OXB-B-1.
// Unlike the hand-authored Quality set they are derived automatically from the
// folders: documentName/documentType = file name, documentCategory = "Testing",
// batch = the folder's batch. Each is a bundled .docx asset (same view/download
// path as the Quality rows). require.context is a no-op outside webpack.
// ═══════════════════════════════════════════════════════════════════════════

const TESTING_CATEGORY = "Testing";

let testing2388Ctx = null;
let testing2785Ctx = null;
try {
  testing2388Ctx = require.context("./qualityDocuments/Testing-2388-OXB-A-1", false, /\.docx$/);
  testing2785Ctx = require.context("./qualityDocuments/Testing-2785-OXB-B-1", false, /\.docx$/);
} catch (e) {
  // Non-webpack environment — Testing docs resolve to none.
}

// Display name = file name without the .docx extension (and without an accidental
// "Copy of " prefix). Document ID = the embedded Analytical Report number when
// present (e.g. AR-IP-2388-113), else the display name.
const cleanTestingName = (key) =>
  key.replace(/^\.\//, "").replace(/\.docx$/i, "").replace(/^Copy of /i, "");
const extractArId = (name) => {
  // Analytical Report code, e.g. AR-IP-2388-113 or AR-P24-2388-103 (the middle
  // segment can be alphanumeric).
  const m = name.match(/AR-[A-Z0-9]+-\d+-\d+/i);
  return m ? m[0] : name;
};

const buildTestingDocs = (ctx, batchNumber, uploadDate) => {
  if (!ctx) return [];
  return ctx.keys().map((key) => {
    const name = cleanTestingName(key);
    const url = ctx(key);
    return {
      // rowId keeps the table row key unique even when two reports share an AR id.
      rowId: `${batchNumber}/${key}`,
      schrockenCustomFileID: null,
      isDemo: true,
      documentId: extractArId(name),
      documentName: name,
      documentType: name,
      fileName: name,
      fileFormat: "DOCX",
      downloadUrl: url,
      viewUrl: url,
      documentCategory: TESTING_CATEGORY,
      productCode: QUALITY_PRODUCT.code,
      productId: QUALITY_PRODUCT.code,
      productDescription: QUALITY_PRODUCT.description,
      batchNumber,
      cmoBatchNumber: batchNumber,
      uploadDate,
      digitizedData: {
        kvpJson: {
          document_type: f(name),
          batch_number: f(batchNumber, 99),
          product_name: f(QUALITY_PRODUCT.description),
          document_category: f(TESTING_CATEGORY),
        },
      },
    };
  });
};

const STATIC_TESTING_DOCUMENTS = [
  ...buildTestingDocs(testing2388Ctx, "2388-OXB-A-1", "2025-05-15"),
  ...buildTestingDocs(testing2785Ctx, "2785-OXB-B-1", "2025-10-16"),
];

// Every static (non-middleware) document surfaced in the repository listing.
const STATIC_DOCUMENTS = [...STATIC_QUALITY_DOCUMENTS, ...STATIC_TESTING_DOCUMENTS];

// Merge extra option entries into an API-derived option list, de-duped by id
// (an API entry with the same id wins).
const mergeOptionsById = (apiList, extraList) => {
  const merged = Array.isArray(apiList) ? [...apiList] : [];
  const seen = new Set(merged.map((o) => String(o.id)));
  (extraList || []).forEach((o) => {
    if (!seen.has(String(o.id))) {
      seen.add(String(o.id));
      merged.push(o);
    }
  });
  return merged;
};

// Filter options contributed by the static Quality documents above.
const STATIC_PRODUCT_OPTION = {
  id: QUALITY_PRODUCT.code,
  label: `${QUALITY_PRODUCT.code} — ${QUALITY_PRODUCT.description}`,
};
const STATIC_BATCH_OPTIONS = QUALITY_BATCH_NUMBERS.map((b) => ({
  id: b,
  label: b,
  productId: QUALITY_PRODUCT.code,
}));
const STATIC_CATEGORY_OPTIONS = [
  { id: QUALITY_CATEGORY, label: QUALITY_CATEGORY },
  { id: TESTING_CATEGORY, label: TESTING_CATEGORY },
];

// Decide whether a static document (Quality or Testing) satisfies the active
// filters / search. `documentType` on the payload carries the Document Category.
const staticDocMatchesQuery = (doc, payload = {}) => {
  const { productId, batchNumber, documentType, searchText } = payload;
  if (productId && productId !== "all" && String(productId) !== String(doc.productCode))
    return false;
  if (batchNumber && batchNumber !== "all" && batchNumber !== doc.batchNumber)
    return false;
  if (documentType && documentType !== "all" && documentType !== doc.documentCategory)
    return false;
  const q = (searchText || "").trim().toLowerCase();
  if (q) {
    const hay = [
      doc.documentName,
      doc.documentId,
      doc.documentType,
      doc.batchNumber,
      doc.documentCategory,
      doc.productDescription,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    if (!hay.includes(q)) return false;
  }
  return true;
};

// ── Filter option lists ─────────────────────────────────────────────────────
/**
 * PRODUCT dropdown — POST /fetchDocumentRepositoryProducts (no request body).
 * The filter value sent back to /fetchDocumentRepository is productId (= productCode).
 * Returns an empty list with status:false on failure (no demo fallback).
 */
export const fetchDocumentRepositoryProducts = async () => {
  const apiBaseUrl = getApiBaseUrl();
  let apiProducts = [];
  if (apiBaseUrl) {
    try {
      const res = await axios.post(`${apiBaseUrl}/fetchDocumentRepositoryProducts`, {});
      const rows = res?.data?.productList || [];
      if (res?.data?.status && Array.isArray(rows)) {
        apiProducts = rows.map((r) => ({
          id: r.productId != null ? String(r.productId) : String(r.productCode),
          label:
            r.productDescription ||
            (r.productName ? `${r.productCode} / ${r.productName}` : String(r.productCode)),
        }));
      }
    } catch (e) {
      // fall through — the static Quality product is still contributed below
    }
  }
  // Always include the product that owns the static Quality documents.
  return { status: true, products: mergeOptionsById(apiProducts, [STATIC_PRODUCT_OPTION]) };
};

/**
 * BATCH dropdown — POST /fetchDocumentRepositoryBatches.
 * Cascades on the selected product: pass productId to narrow batches, or omit
 * it (product === "all") to get every batch. The filter value sent back to
 * /fetchDocumentRepository is batchNumber. Returns an empty list with
 * status:false on failure (no demo fallback).
 */
export const fetchDocumentRepositoryBatches = async (productId) => {
  const apiBaseUrl = getApiBaseUrl();
  const hasProduct = productId && productId !== "all";

  let apiBatches = [];
  if (apiBaseUrl) {
    try {
      const res = await axios.post(
        `${apiBaseUrl}/fetchDocumentRepositoryBatches`,
        hasProduct ? { productId } : {}
      );
      const rows = res?.data?.batchList || [];
      if (res?.data?.status && Array.isArray(rows)) {
        apiBatches = rows.map((b) => ({
          id: b.batchNumber,
          label: b.vendorBatchNumber || b.batchNumber,
          productId: b.productCode != null ? String(b.productCode) : undefined,
        }));
      }
    } catch (e) {
      // fall through — the static Quality batches are still contributed below
    }
  }
  // Contribute the Quality-document batches when they belong to the selected
  // product (or when no product filter is active).
  const staticBatches =
    !hasProduct || String(productId) === QUALITY_PRODUCT.code ? STATIC_BATCH_OPTIONS : [];
  return { status: true, batches: mergeOptionsById(apiBatches, staticBatches) };
};

/**
 * DOCUMENT CATEGORY dropdown — GET /fetchDocumentCategories/true.
 * The filter value sent back to /fetchDocumentRepository is documentCategory.
 * Returns an empty list with status:false on failure (no demo fallback).
 */
export const fetchDocumentRepositoryDocumentCategories = async () => {
  const apiBaseUrl = getApiBaseUrl();
  let apiCategories = [];
  if (apiBaseUrl) {
    try {
      const res = await axios.get(`${apiBaseUrl}/fetchDocumentCategories/true`);
      const rows = res?.data?.documentCategoryList || [];
      if (Array.isArray(rows)) {
        apiCategories = rows
          .map((c) => ({
            id: c.documentCategoryName || c.documentCategoryType,
            label: c.documentCategoryName || c.documentCategoryType,
          }))
          // Hide the "Purchasing" and "ERP" categories from the dropdown.
          .filter((c) => !["Purchasing", "ERP"].includes(c.id));
      }
    } catch (e) {
      // fall through — the static "Quality" category is still contributed below
    }
  }
  // Always expose the "Quality" category used by the batch document folders.
  return {
    status: true,
    documentTypes: mergeOptionsById(apiCategories, STATIC_CATEGORY_OPTIONS),
  };
};

/**
 * CMO dropdown — POST /fetchCMOMembers (the SAME endpoint the Purchase Order
 * Collaboration CMO filter uses). This endpoint is registered only on the
 * sponsor (Pharma Co / CMS) router, so the URL uses the literal "Pharma Co"
 * collab segment exactly like the PO Collaboration `cmoMembersList` action —
 * NOT getApiBaseUrl() (whose collabType segment would be "CMO"/"SMO" and either
 * 404 or hit the wrong router). The value used to filter is organization_id;
 * the label is organization_name.
 *
 * CMO-side users (collabType "CMO") are a single CMO org with nothing to pick,
 * so the caller skips this for them. Any non-200 / failure resolves to an empty
 * CMO list so the screen still renders (graceful degradation).
 */
export const fetchDocumentRepositoryCMOs = async () => {
  const appName = localStorage.getItem("appName");
  const network = localStorage.getItem("network");
  const roleName = localStorage.getItem("roleName");
  const collabType = localStorage.getItem("collabType");
  if (!appName || !network || !roleName) return { status: true, cmos: [] };
  try {
    const res = await axios.post(
      `${appUrl}/${appName}/${network}/Pharma Co/${roleName}/fetchCMOMembers`,
      {
        collabType,
        roleName,
        organizationName: localStorage.getItem("organizationName"),
        ecosystemId: localStorage.getItem("ecosystemId"),
      }
    );
    const rows = res?.data?.CMOMembersWithId || [];
    if (res?.data?.status && Array.isArray(rows)) {
      // A CMO with multiple vendor plants appears once per plant — de-dupe by
      // organization_id (the value used to filter).
      const seen = new Set();
      const cmos = [];
      rows.forEach((c) => {
        if (!c || c.organization_id == null) return;
        const id = String(c.organization_id);
        if (seen.has(id)) return;
        seen.add(id);
        cmos.push({ id, label: c.organization_name || id });
      });
      return { status: true, cmos };
    }
  } catch (e) {
    // 404 for CMO-side users or any failure — degrade to an empty CMO list.
  }
  return { status: true, cmos: [] };
};

/**
 * PO dropdown — POST /fetchDocumentRepositoryPOs.
 * Returns the distinct PO numbers (scheduleAgreementNumber). Cascades on the
 * selected CMO: pass cmoId to narrow the list to that CMO's POs, or omit it
 * (CMO === "all") for every PO. The filter value sent back to
 * /fetchDocumentRepository is poNumber. Returns an empty list on failure.
 */
export const fetchDocumentRepositoryPOs = async (cmoId) => {
  const apiBaseUrl = getApiBaseUrl();
  const hasCmo = cmoId && cmoId !== "all";
  if (!apiBaseUrl) return { status: true, pos: [] };
  try {
    const res = await axios.post(
      `${apiBaseUrl}/fetchDocumentRepositoryPOs`,
      hasCmo ? { cmoId } : {}
    );
    const rows = res?.data?.poList || [];
    if (res?.data?.status && Array.isArray(rows)) {
      const seen = new Set();
      const pos = [];
      rows.forEach((r) => {
        const po = r.poNumber;
        if (!po || seen.has(po)) return;
        seen.add(po);
        pos.push({ id: po, label: po });
      });
      return { status: true, pos };
    }
  } catch (e) {
    // fall through — empty PO list (no demo fallback)
  }
  return { status: true, pos: [] };
};

/**
 * Convenience loader for the initial render: fetches the product and category
 * dropdowns plus the full (unscoped) batch and PO lists in parallel. When the
 * product filter changes, the caller re-fetches batches alone via
 * fetchDocumentRepositoryBatches(productId); when the CMO filter changes it
 * re-fetches POs alone via fetchDocumentRepositoryPOs(cmoId) — honouring the
 * cascades. (CMO options are loaded separately, gated on collabType.)
 */
export const fetchDocumentRepositoryFilters = async () => {
  const [products, batches, documentTypes, pos] = await Promise.all([
    fetchDocumentRepositoryProducts(),
    fetchDocumentRepositoryBatches(),
    fetchDocumentRepositoryDocumentCategories(),
    fetchDocumentRepositoryPOs(),
  ]);
  return {
    status: true,
    products: products.products,
    batches: batches.batches,
    documentTypes: documentTypes.documentTypes,
    pos: pos.pos,
  };
};

// ── Dashboard table ─────────────────────────────────────────────────────────
/**
 * Fetch the document listing for the dashboard table.
 * POST /fetchDocumentRepository (query-based middleware endpoint).
 * Returns an empty list with status:false when the middleware is unavailable or
 * the call fails — no demo / placeholder rows are shown.
 */
export const fetchDocumentRepository = async (payload = {}) => {
  const apiBaseUrl = getApiBaseUrl();

  // Static Quality/Testing documents that satisfy the active filters. They are
  // shown in addition to any middleware rows (or on their own when the middleware
  // is unavailable) so they appear for their batch and under their category.
  const staticMatches = STATIC_DOCUMENTS.filter((d) =>
    staticDocMatchesQuery(d, payload)
  );

  // Build the request body with only the active filters. All fields are
  // optional; an empty object {} means "no filters" (all rows / Clear filters).
  // NOTE: cmoId / poNumber are forwarded to the middleware body ONLY — they are
  // deliberately NOT part of staticDocMatchesQuery, because the static Quality/
  // Testing docs carry no CMO/PO fields and gating on them would make every
  // static row vanish the moment a CMO or PO is selected.
  const body = {};
  if (payload.cmoId && payload.cmoId !== "all") body.cmoId = payload.cmoId;
  if (payload.productId && payload.productId !== "all") body.productId = payload.productId;
  if (payload.poNumber && payload.poNumber !== "all") body.poNumber = payload.poNumber;
  if (payload.batchNumber && payload.batchNumber !== "all") body.batchNumber = payload.batchNumber;
  if (payload.documentType && payload.documentType !== "all") body.documentCategory = payload.documentType;
  if (payload.searchText && payload.searchText.trim()) body.searchText = payload.searchText.trim();

  if (apiBaseUrl) {
    try {
      const res = await axios.post(`${apiBaseUrl}/fetchDocumentRepository`, body);
      const list = res?.data?.documentList;
      // status is checked first: endpoints return HTTP 200 even on failure.
      if (res?.data?.status && Array.isArray(list)) {
        const apiRows = list.filter((val) => val.productCode !== "");
        return {
          status: true,
          documentList: [...staticMatches, ...apiRows],
          totalCount:
            (res.data.totalCount != null ? res.data.totalCount : apiRows.length) +
            staticMatches.length,
        };
      }
    } catch (e) {
      // fall through — still surface the static Quality documents below
    }
  }

  // Middleware unavailable or the call failed: show the matching static Quality
  // documents (status:true so no error banner). With no static matches, keep the
  // error state so non-Quality queries still report the middleware failure.
  return {
    status: staticMatches.length > 0,
    documentList: staticMatches,
    totalCount: staticMatches.length,
  };
};

// ── Persist selected filters (best-effort) ──────────────────────────────────
export const saveDocumentRepositoryFilters = async (payload) => {
  try {
    localStorage.setItem("documentRepositoryFilters", JSON.stringify(payload));
  } catch (e) {
    /* ignore */
  }
  const apiBaseUrl = getApiBaseUrl();
  if (apiBaseUrl) {
    try {
      await axios.post(`${apiBaseUrl}/saveDocumentRepositoryFilters`, payload);
    } catch (e) {
      /* saving filters is best-effort; local copy already persisted */
    }
  }
  return { status: true };
};

// ── File download / view (downloadCustomFile) ───────────────────────────────
/**
 * Download a file's raw bytes from the middleware.
 * POST /downloadCustomFile { schrockenCustomFileID }
 * Returns { status, fileName, byteArray } — the response is a JSON Buffer
 * (fileData.data), converted to a Uint8Array for Blob use by the caller.
 */
export const fetchCustomFileBytes = async (schrockenCustomFileID) => {
  const apiBaseUrl = getApiBaseUrl();
  if (!apiBaseUrl || schrockenCustomFileID == null) {
    return { status: false };
  }
  const res = await axios.post(`${apiBaseUrl}/downloadCustomFile`, {
    schrockenCustomFileID,
  });
  if (res?.data?.status && res.data.fileData?.data) {
    return {
      status: true,
      fileName: res.data.fileName,
      byteArray: new Uint8Array(res.data.fileData.data),
    };
  }
  return { status: false };
};

/**
 * Fetch the full batch-details payload for a document row so the shared
 * CommonDigitizedDocument viewer can open with the same shape it uses from
 * Batch Details (digitizedExtractedData + customFilesList + confidenceLevels).
 * POST /fetchDeliveryScheduleBatchDetails — the exact endpoint the Batch
 * Details "View Digitized Information" flow uses. Returns { status, data }.
 */
export const fetchBatchDetailsForReview = async ({
  schrockenDeliveryScheduleBatchID,
  batchNumber,
  poNumber,
}) => {
  const apiBaseUrl = getApiBaseUrl();
  if (!apiBaseUrl || schrockenDeliveryScheduleBatchID == null) {
    return { status: false };
  }
  try {
    const res = await axios.post(
      `${apiBaseUrl}/fetchDeliveryScheduleBatchDetails`,
      {
        schrockenDeliveryScheduleBatchID,
        batchNumber,
        cmoBatchNumber: batchNumber,
        poNumber,
      }
    );
    if (res && res.data && res.data.status && res.data.data) {
      return { status: true, data: res.data.data };
    }
  } catch (e) {
    // fall through — caller builds a lightweight view from row data
  }
  return { status: false };
};

/**
 * Trigger a browser download of a static-asset file (the Quality documents under
 * public/qualityDocuments). Fetches the URL and saves it under `fileName`.
 * Used only for the static Quality rows; API rows go through downloadCustomFileById.
 * Returns { status } so the caller can surface a snackbar.
 */
export const downloadStaticFile = async (url, fileName) => {
  try {
    const res = await fetch(url);
    if (!res.ok) return { status: false };
    const blob = await res.blob();
    const objUrl = window.URL.createObjectURL(blob);
    const a = window.document.createElement("a");
    a.href = objUrl;
    a.download = fileName || "document";
    window.document.body.appendChild(a);
    a.click();
    window.document.body.removeChild(a);
    window.URL.revokeObjectURL(objUrl);
    return { status: true };
  } catch (e) {
    return { status: false };
  }
};

/**
 * Trigger a browser download of a document via /downloadCustomFile.
 * Returns { status } so the caller can surface a snackbar.
 */
export const downloadCustomFileById = async (schrockenCustomFileID, fallbackName) => {
  try {
    const result = await fetchCustomFileBytes(schrockenCustomFileID);
    if (!result.status) return { status: false };
    const blob = new Blob([result.byteArray], { type: "application/octet-stream" });
    const url = window.URL.createObjectURL(blob);
    const a = window.document.createElement("a");
    a.href = url;
    a.download = result.fileName || `${fallbackName || schrockenCustomFileID}.pdf`;
    window.document.body.appendChild(a);
    a.click();
    window.document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    return { status: true };
  } catch (e) {
    return { status: false };
  }
};
